import classnames from 'classnames'
import { useRef, createRef, RefObject, useEffect, FC, useState } from 'react'

import Icon, { IconTypes } from '~ui/Icon'

import styles from './SegmentedControl.module.scss'

export type Item = {
  label: string
  value: string
  icon?: IconTypes
}

export type ToggleProps = {
  items: Item[]
  value?: string
  onChange?(value: string): void
  disabled?: boolean
  uppercase?: boolean
}

const Toggle: FC<ToggleProps> = ({ onChange, items, value, disabled, uppercase }) => {
  const toggleRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [select, setSelect] = useState(value || items[0].value)

  const refs: { [k: string]: RefObject<HTMLDivElement> } = items.reduce((acc, { value: v }) => {
    acc[v] = createRef<HTMLDivElement>()
    return acc
  }, {})

  const selectedItem = items.find(({ value: v }) => v === select)

  useEffect(() => {
    const childPos = refs[select].current.getBoundingClientRect()
    const parentPos = wrapperRef.current.getBoundingClientRect()

    const relativeLeft = childPos.left - parentPos.left

    toggleRef.current.style.width = `${childPos.width}px`
    toggleRef.current.style.transform = `translate(${relativeLeft}px)`
  }, [refs, select])

  useEffect(() => {
    if (value) setSelect(value)
  }, [value])

  return (
    <div
      className={classnames(styles.wrapper, [
        { [styles.disabled]: disabled, [styles.uppercase]: uppercase },
      ])}
    >
      <div ref={wrapperRef} className={styles.items}>
        {items.map(({ label, value: v, icon }) => (
          <div
            key={v}
            ref={refs[v]}
            className={classnames(styles.item, {
              [styles['item--active']]: selectedItem.value === v,
            })}
            onClick={() => {
              setSelect(v)
              onChange?.(v)
            }}
            aria-hidden
          >
            {icon && (
              <Icon name={icon as IconTypes} fill="currentColor" height="0.7em" width="0.7em" />
            )}
            <span>{label}</span>
          </div>
        ))}
      </div>
      <div className={styles.indicator} ref={toggleRef}>
        {selectedItem.icon && (
          <Icon
            name={selectedItem.icon as IconTypes}
            fill="currentColor"
            height="0.7em"
            width="0.7em"
          />
        )}
        <span>{selectedItem.label}</span>
      </div>
    </div>
  )
}

export default Toggle
